// /users — admin user browse (clean data-table console).
//
// Two axes (FACTS PACK §3a):
//  • BROWSE: status filter, opaque-cursor prev/next (useUserBrowse). region 假
//    篩選器已依 D81③ 移除(伺服端瀏覽模式忽略 region)。
//  • LOOKUP by email: GET /v3/admin/users?email= returns a FLAT user object (not a
//    list). Email search short-circuits the browse list and renders the single hit
//    (or an empty state). Cleared search → back to browse.
//
// Styling reuses the shared console primitives (global.css .toolbar/.table-wrap/
// .data-table/.pill/.page-*) + the shared adm-* toolbar/table helpers
// (_shared/shared.css). Only the sortable-header affordance is feature-local (u-sort*).
//
// Client-side column sort applies to the CURRENT page only (the API is cursor-paged
// with no sort param); the table meta says so.

import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EmptyState, ErrorState, LoadingState } from '../../components/StateBlock';
import { ApiError } from '../../lib/api';
import { formatTimestampUs } from '../../lib/format';
import { CopyableId } from '../../components/CopyableId';
import { roleClass, roleLabel, userStatusLabel, userStatusTone } from './userModel';
import { lookupUserByEmail } from './usersApi';
import type { AdminUser } from './usersApi';
import { useUserBrowse } from './useUserBrowse';
import type { BrowseFilters } from './useUserBrowse';
import { listEcosystems } from '../ecosystem/ecosystemApi';
import type { Ecosystem } from '../ecosystem/ecosystemApi';
import './users.css';

const STATUSES = [
  { value: '', label: '現有帳號' },
  { value: 'active', label: '使用中' },
  { value: 'pending_verification', label: '待驗證' },
  { value: 'suspended', label: '已停權' },
  { value: 'banned', label: '已封禁' },
  { value: 'deleting', label: '刪除中' },
  { value: 'deleted', label: '已刪除' },
];

// /users owns App (end-user) accounts only — developers are governed on
// /organizations (開發者組織), maintainers on /team (S9); neither is browsed here.

type SortKey = 'email' | 'region' | 'base_ecosystem_name' | 'role' | 'status' | 'created_at';
type SortDir = 'asc' | 'desc';

// 生態系 (eco-*, /ecosystems org-interop object) options for the filter dropdown.
// The filter is EFFECTIVE-membership (原 OR 加入; common#24, owner 2026-07-12):
// picking an ecosystem keeps users whose base OR joined set includes it. Distinct
// from the 08 §2.1 auth-pool family axis. Best-effort: a load failure just leaves
// "全部生態系". active-only — a suspended ecosystem is not a useful browse facet.
function useEcosystems(): Ecosystem[] {
  const [ecosystems, setEcosystems] = useState<Ecosystem[]>([]);
  useEffect(() => {
    let alive = true;
    listEcosystems({ status: 'active' })
      .then((rows) => alive && setEcosystems(rows ?? []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  return ecosystems;
}

/** 原生態系 cell — the user's base ecosystem (eco-* 屬性模型 base_ecosystem_id).
 *  eco-kinio-ai = 平台 (neutral pill); an org ecosystem = customer (info pill).
 *  Falls back to the 品牌家族 name (fam↔eco are 1:1), then '—'. */
function OriginEcosystemCell({ user }: { user: AdminUser }) {
  const name = user.base_ecosystem_name || user.family_name;
  const id = user.base_ecosystem_id || user.family_id;
  if (!name && !id) return <span className="text-tertiary">—</span>;
  const platform = !id || id === 'eco-kinio-ai' || id === 'fam-platform';
  return (
    <span className={`pill pill-${platform ? 'neutral' : 'info'}`} title={id || undefined}>
      {name || id}
    </span>
  );
}

/** 加入的生態系 cell — explicit joined ecosystems (effective set minus base) plus the
 *  kinio.ai platform-union flag (the union is a flag, never enumerated). Platform
 *  users don't list the union — they ARE it. '—' when nothing joined. */
function JoinedEcosystemCell({ user }: { user: AdminUser }) {
  const joined = user.joined_ecosystems ?? [];
  const showUnion = !!user.platform_union && user.base_ecosystem_id !== 'eco-kinio-ai';
  if (joined.length === 0 && !showUnion) return <span className="text-tertiary">—</span>;
  return (
    <span className="u-eco-joined">
      {joined.map((e) => (
        <span key={e.id} className="pill pill-info" title={e.id}>
          {e.name || e.id}
        </span>
      ))}
      {showUnion && (
        <span className="u-eco-union-flag" title="加入 kinio.ai 平台聯盟(access-time 互通,不列舉成員)">
          <span className="u-eco-union-check" aria-hidden="true">✓</span>
          平台聯盟
        </span>
      )}
    </span>
  );
}

function StatusPill({ status }: { status: string | undefined }) {
  return <span className={`pill pill-${userStatusTone(status)}`}>{userStatusLabel(status)}</span>;
}

function sortUsers(rows: AdminUser[], key: SortKey, dir: SortDir): AdminUser[] {
  const factor = dir === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (key === 'created_at') return ((a.created_at ?? 0) - (b.created_at ?? 0)) * factor;
    const av = (a[key] ?? '').toString().toLowerCase();
    const bv = (b[key] ?? '').toString().toLowerCase();
    return av.localeCompare(bv) * factor;
  });
}

export default function UsersPage() {
  const navigate = useNavigate();

  // committed filters (drive the browse hook). identity is pinned to 'app' — /users
  // is App-users-only; developers live on /organizations, maintainers on /team.
  const [filters, setFilters] = useState<BrowseFilters>({ status: '', family: '', ecosystem: '', identity: 'app' });
  const browse = useUserBrowse(filters);
  const ecosystems = useEcosystems();

  // email lookup (separate axis)
  const [emailInput, setEmailInput] = useState('');
  const [lookup, setLookup] = useState<{ loading: boolean; error: unknown; result: AdminUser | null; term: string } | null>(null);

  // client-side sort of the current page
  const [sortKey, setSortKey] = useState<SortKey>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const open = (id: string) => navigate(`/users/${encodeURIComponent(id)}`);
  const sortedRows = useMemo(() => (browse.users ? sortUsers(browse.users, sortKey, sortDir) : null), [browse.users, sortKey, sortDir]);

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'created_at' ? 'desc' : 'asc');
    }
  }

  function sortIndicator(key: SortKey) {
    if (sortKey !== key) return null;
    return <span className="u-sort-ind">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  }

  async function runLookup(e?: FormEvent) {
    e?.preventDefault();
    const term = emailInput.trim();
    if (!term) {
      setLookup(null);
      return;
    }
    setLookup({ loading: true, error: null, result: null, term });
    try {
      const user = await lookupUserByEmail(term);
      setLookup({ loading: false, error: null, result: user, term });
    } catch (err) {
      setLookup({ loading: false, error: err instanceof ApiError ? err : new Error('查詢失敗'), result: null, term });
    }
  }

  function clearLookup() {
    setEmailInput('');
    setLookup(null);
  }

  const inLookup = lookup !== null;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">使用者</h1>
          <p className="page-subtitle">
            瀏覽 App 使用者帳號、查看終端帳號全貌、執行帳號處置。開發者帳號於 <Link to="/organizations">開發者組織</Link> 管理、維運者帳號於 <Link to="/team">維運團隊</Link> 管理。
          </p>
        </div>
        <button className="btn-ghost btn-sm" onClick={() => browse.reload()} disabled={browse.loading || inLookup}>
          {browse.loading && !inLookup ? '載入中…' : '重新整理'}
        </button>
      </div>

      {/* ---- App 使用者說明(開發者移至 /organizations、不在此瀏覽)---- */}
      <div className="u-identity-bar">
        <p className="text-tertiary u-identity-hint">
          一般 App 使用者帳號（於 Home／Aura 等 App 端註冊登入）。同一 Email 在不同「生態系」註冊視為不同帳號。
        </p>
      </div>

      {/* ---- filter / search toolbar:與 /devices 姊妹頁一致,採 .filter-bar is-sticky ---- */}
      <form className="filter-bar is-sticky" onSubmit={runLookup} role="search">
        <label className="adm-field" style={{ flex: '1 1 280px' }}>
          <span className="text-secondary adm-field-label">以電子郵件精準查詢</span>
          <input
            type="search"
            placeholder="user@example.com"
            aria-label="以 Email 查詢使用者"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            style={{ width: '100%' }}
          />
        </label>
        <button type="submit" className="btn-primary" disabled={!emailInput.trim()}>
          查詢
        </button>
        {inLookup && (
          <button type="button" className="btn-ghost" onClick={clearLookup}>
            清除
          </button>
        )}

        <div className="adm-toolbar-spacer" />

        <label className="adm-field">
          <span className="text-secondary adm-field-label">生態系</span>
          <select value={filters.ecosystem} disabled={inLookup} onChange={(e) => setFilters((f) => ({ ...f, ecosystem: e.target.value }))}>
            <option value="">全部生態系</option>
            {ecosystems.map((eco) => (
              <option key={eco.ecosystem_id} value={eco.ecosystem_id}>
                {eco.display_name || eco.ecosystem_id}
              </option>
            ))}
          </select>
        </label>
        <label className="adm-field">
          <span className="text-secondary adm-field-label">狀態</span>
          <select value={filters.status} disabled={inLookup} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
      </form>

      {/* ---- LOOKUP result view ---- */}
      {inLookup && (
        <>
          {lookup!.loading && (
            <div className="card">
              <LoadingState label="查詢中…" />
            </div>
          )}
          {!lookup!.loading && lookup!.error != null && <ErrorState error={lookup!.error} onRetry={() => void runLookup()} />}
          {!lookup!.loading && lookup!.error == null && !lookup!.result && (
            <EmptyState
              title="找不到符合的使用者"
              detail={
                <>
                  沒有 Email 為「<strong>{lookup!.term}</strong>」的使用者。Email 查詢為精準比對，請確認完整地址，或清除查詢改用清單瀏覽。
                </>
              }
              action={
                <button className="btn-ghost btn-sm" onClick={clearLookup}>
                  返回清單
                </button>
              }
            />
          )}
          {!lookup!.loading && lookup!.result && (
            <div className="table-wrap">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>電子郵件</th>
                    <th>顯示名稱</th>
                    <th>使用者 ID</th>
                    <th>基礎生態系</th>
                    <th>加入生態系</th>
                    <th>地區</th>
                    <th>角色</th>
                    <th>狀態</th>
                    <th>建立時間</th>
                  </tr>
                </thead>
                <tbody>
                  <UserRow user={lookup!.result} onOpen={open} />
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ---- BROWSE table view ---- */}
      {!inLookup && (
        <>
          {browse.loading && !sortedRows && (
            <div className="card">
              <LoadingState label="載入使用者清單…" />
            </div>
          )}

          {browse.error != null && !browse.loading && <ErrorState error={browse.error} onRetry={() => browse.reload()} />}

          {/* 表格 + 合併 sticky pager footer(鏡像 /devices):單一 footer 取代原本
              「表格上方 meta + 下方 pager」兩處。footer 常駐於 error==null && sortedRows,
              即使本頁 0 筆(cursor 仍活)也保留上一頁/下一頁,操作者不會被困住。 */}
          {browse.error == null && sortedRows && (
            <div className="table-wrap u-browse-wrap">
              {sortedRows.length > 0 ? (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th className="u-sortable" onClick={() => toggleSort('email')}>
                        電子郵件 {sortIndicator('email')}
                      </th>
                      <th>顯示名稱</th>
                      <th>使用者 ID</th>
                      <th className="u-sortable" onClick={() => toggleSort('base_ecosystem_name')}>
                        原生態系 {sortIndicator('base_ecosystem_name')}
                      </th>
                      <th>加入生態系</th>
                      <th className="u-sortable" onClick={() => toggleSort('region')}>
                        地區 {sortIndicator('region')}
                      </th>
                      <th className="u-sortable" onClick={() => toggleSort('role')}>
                        角色 {sortIndicator('role')}
                      </th>
                      <th className="u-sortable" onClick={() => toggleSort('status')}>
                        狀態 {sortIndicator('status')}
                      </th>
                      <th className="u-sortable" onClick={() => toggleSort('created_at')}>
                        建立時間 {sortIndicator('created_at')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRows.map((u) => (
                      <UserRow key={u.user_id} user={u} onOpen={open} />
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="u-browse-empty">
                  <EmptyState
                    title="本頁沒有符合條件的使用者"
                    detail={browse.hasNext ? '掃描窗內無符合列——資料可能在後面幾頁,用下方「下一頁」繼續掃描,或放寬篩選。' : '試著放寬地區／狀態／生態系篩選,或用上方 Email 查詢。'}
                  />
                </div>
              )}

              <div className="u-pager-footer">
                <span className="text-secondary u-pager-count">
                  本頁 {sortedRows.length} 筆{sortedRows.length > 0 ? ' · 排序僅作用於目前頁面' : ''}
                </span>
                <div className="u-pager-nav">
                  <button className="btn-ghost btn-sm" onClick={browse.prev} disabled={!browse.hasPrev || browse.loading}>
                    ← 上一頁
                  </button>
                  <span className="adm-mono u-pager-page">第 {browse.pageIndex + 1} 頁</span>
                  <button className="btn-ghost btn-sm" onClick={browse.next} disabled={!browse.hasNext || browse.loading}>
                    下一頁 →
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

function UserRow({ user, onOpen }: { user: AdminUser; onOpen: (id: string) => void }) {
  return (
    <tr
      className="clickable"
      tabIndex={0}
      onClick={() => onOpen(user.user_id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen(user.user_id);
      }}
    >
      <td className="adm-cell-strong">{user.email || '—'}</td>
      <td>{user.display_name || '—'}</td>
      <td>
        <CopyableId value={user.user_id} className="text-tertiary" />
      </td>
      <td>
        <OriginEcosystemCell user={user} />
      </td>
      <td>
        <JoinedEcosystemCell user={user} />
      </td>
      <td>{user.region || '—'}</td>
      <td>
        <span className={`pill u-role-pill ${roleClass(user.role) || 'pill-neutral'}`}>{roleLabel(user.role)}</span>
      </td>
      <td>
        <StatusPill status={user.status} />
      </td>
      <td className="adm-mono">{formatTimestampUs(user.created_at, { second: undefined })}</td>
    </tr>
  );
}
